import { config } from "../config";
import { getBaseHeaders, handleApiError } from "./utils";
import { clearUserId } from "./validation";
import { isNative } from "@/utils/platform";
import { nativeTokenStore } from "./nativeTokenStore";

class TokenManager {
  private refreshPromise: Promise<void> | null = null;
  private isLoggingOut: boolean = false;
  private refreshFailed: boolean = false;

  private readonly REFRESH_LOCK_KEY = 'buyfors_refresh_lock';
  private readonly REFRESH_RESULT_KEY = 'buyfors_refresh_result';
  private readonly LOCK_TIMEOUT = 5000;

  setLoggingOut(value: boolean): void {
    this.isLoggingOut = value;
  }

  resetRefreshFailed(): void {
    this.refreshFailed = false;
  }

  constructor() {
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('storage', (event) => {
        if (event.key === this.REFRESH_RESULT_KEY && event.newValue) {
          try {
            const result = JSON.parse(event.newValue);
            if (Date.now() - result.timestamp < this.LOCK_TIMEOUT) {
              if (result.status === 'success') {
                this.refreshFailed = false;
              } else if (result.error === 'REFRESH_TOKEN_EXPIRED') {
                this.refreshFailed = true;
              }
            }
          } catch (e) {
            // ignore
          }
        }
      });
    }
  }

  private clearAuthCookies(): void {
    const cookiesToClear = ['access_token', 'refresh_token', 'session_token', 'user_id'];
    const domains = window.location.hostname.includes('localhost')
      ? ['localhost']
      : ['.buyfors.com', 'buyfors.com', 'www.buyfors.com', 'api.buyfors.com'];
    const paths = ['/', '/api'];

    cookiesToClear.forEach(cookieName => {
      domains.forEach(domain => {
        paths.forEach(path => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
        });
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      });
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    this.cleanupStaleSyncData();
    clearUserId();
  }

  private cleanupStaleSyncData(): void {
    const lock = localStorage.getItem(this.REFRESH_LOCK_KEY);
    if (lock) {
      const lockTime = parseInt(lock);
      if (isNaN(lockTime) || Date.now() - lockTime > 10000) {
        localStorage.removeItem(this.REFRESH_LOCK_KEY);
        localStorage.removeItem(this.REFRESH_RESULT_KEY);
      }
    }

    const result = localStorage.getItem(this.REFRESH_RESULT_KEY);
    if (result) {
      try {
        const parsed = JSON.parse(result);
        if (!parsed.timestamp || Date.now() - parsed.timestamp > 10000) {
          localStorage.removeItem(this.REFRESH_RESULT_KEY);
        }
      } catch (e) {
        localStorage.removeItem(this.REFRESH_RESULT_KEY);
      }
    }
  }

  public async forceCleanup(): Promise<void> {
    if (isNative()) {
      await nativeTokenStore.clear();
    } else {
      this.clearAuthCookies();
    }
    this.refreshFailed = false;
    this.refreshPromise = null;
  }

  async refreshTokens(): Promise<void> {
    if (this.isLoggingOut) {
      throw new Error('User is logging out');
    }

    if (this.refreshFailed) {
      throw new Error('REFRESH_TOKEN_EXPIRED');
    }

    if (isNative()) {
      if (this.refreshPromise) {
        return this.refreshPromise;
      }
      this.refreshPromise = this.performRefresh();
      try {
        await this.refreshPromise;
      } finally {
        this.refreshPromise = null;
      }
      return;
    }

    const existingLock = localStorage.getItem(this.REFRESH_LOCK_KEY);
    if (existingLock) {
      const lockTime = parseInt(existingLock);
      if (Date.now() - lockTime < this.LOCK_TIMEOUT) {
        return this.waitForRefreshResult();
      }
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    localStorage.setItem(this.REFRESH_LOCK_KEY, Date.now().toString());
    this.refreshPromise = this.performRefresh();

    try {
      await this.refreshPromise;
      localStorage.setItem(this.REFRESH_RESULT_KEY, JSON.stringify({
        status: 'success',
        timestamp: Date.now()
      }));
    } catch (error) {
      localStorage.setItem(this.REFRESH_RESULT_KEY, JSON.stringify({
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: Date.now()
      }));
      throw error;
    } finally {
      this.refreshPromise = null;
      localStorage.removeItem(this.REFRESH_LOCK_KEY);
    }
  }

  private async waitForRefreshResult(): Promise<void> {
    const maxWait = this.LOCK_TIMEOUT;
    const checkInterval = 100;
    let waited = 0;

    while (waited < maxWait) {
      const lock = localStorage.getItem(this.REFRESH_LOCK_KEY);
      if (!lock) {
        const result = localStorage.getItem(this.REFRESH_RESULT_KEY);
        if (result) {
          const parsed = JSON.parse(result);
          if (Date.now() - parsed.timestamp < this.LOCK_TIMEOUT) {
            if (parsed.status === 'error') {
              if (parsed.error === 'REFRESH_TOKEN_EXPIRED') {
                this.refreshFailed = true;
              }
              throw new Error(parsed.error);
            }
            return;
          }
        }
        return this.refreshTokens();
      }

      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }

    localStorage.removeItem(this.REFRESH_LOCK_KEY);
    return this.refreshTokens();
  }

  private async performRefresh(retryCount: number = 0): Promise<void> {
    const MAX_RETRIES = 1;
    const TIMEOUT = 20000;

    try {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), TIMEOUT);

      const native = isNative();
      const headers: Record<string, string> = { ...getBaseHeaders() };

      if (native) {
        // A share extension may have refreshed the session out-of-process
        // while the app was backgrounded. Adopt its rotated token *here*,
        // right before we read it — otherwise we'd refresh with the stale
        // token, which the backend flags as reuse and ends the session.
        await nativeTokenStore.syncFromShared();
        const rt = await nativeTokenStore.getRefreshToken();
        if (!rt) {
          clearTimeout(timeoutId);
          this.refreshFailed = true;
          throw new Error('REFRESH_TOKEN_EXPIRED');
        }
        headers['X-Refresh-Token'] = rt;
      }

      const fetchInit: RequestInit = {
        method: 'POST',
        headers,
        signal: abortController.signal,
      };

      if (!native) {
        fetchInit.credentials = 'include';
      }

      const response = await fetch(`${config.API_URL}/user/refresh`, fetchInit);

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          this.refreshFailed = true;
          if (native) {
            await nativeTokenStore.clear();
          } else {
            this.clearAuthCookies();
          }
          throw new Error('REFRESH_TOKEN_EXPIRED');
        }
        if (response.status === 409) {
          if (retryCount < MAX_RETRIES) {
            const backoffDelay = 200 * (retryCount + 1);
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            return this.performRefresh(retryCount + 1);
          }
          throw new Error('Concurrent refresh detected. Please try again.');
        }
        await handleApiError(response, 'Token refresh');
      }

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        try {
          const text = await response.text();
          data = text ? JSON.parse(text) : null;
        } catch {
          data = null;
        }
      }

      const accessToken = data?.access_token ?? data?.accessToken;
      const refreshToken = data?.refresh_token ?? data?.refreshToken;
      const userId = data?.user_id ?? data?.userId;

      if (native) {
        if (!accessToken || !refreshToken) {
          throw new Error(`Refresh response missing tokens (status=${response.status}, keys=${data ? Object.keys(data).join(',') : 'null'})`);
        }
        await nativeTokenStore.save({
          access_token: accessToken,
          refresh_token: refreshToken,
          user_id: Number(userId),
        });
      }

      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        if (retryCount < MAX_RETRIES) {
          const backoffDelay = 500;
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return this.performRefresh(retryCount + 1);
        }
      }
      throw error;
    }
  }

  async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    if (this.isLoggingOut) {
      throw new Error('User is logging out');
    }

    const isFormData = options.body instanceof FormData;
    const native = isNative();

    const baseHeaders = isFormData
      ? { 'x-api-token': getBaseHeaders()['x-api-token'] }
      : getBaseHeaders();

    const headers: Record<string, string> = {
      ...baseHeaders,
      ...(options.headers as Record<string, string> | undefined),
    };

    if (!options.body) {
      delete headers['Content-Type'];
    }

    if (native) {
      const access = await nativeTokenStore.getAccessToken();
      if (access) {
        headers['Authorization'] = `Bearer ${access}`;
      }
    }

    const requestOptions: RequestInit = {
      ...options,
      headers,
    };

    if (!native) {
      requestOptions.credentials = 'include';
    }

    let response = await fetch(url, requestOptions);

    if (response.status === 401 && !this.isLoggingOut && !this.refreshFailed) {
      try {
        await this.refreshTokens();
        if (native) {
          const access = await nativeTokenStore.getAccessToken();
          if (access) {
            headers['Authorization'] = `Bearer ${access}`;
          }
        }
        response = await fetch(url, { ...requestOptions, headers });
      } catch (refreshError) {
        if (refreshError instanceof Error && refreshError.message === 'REFRESH_TOKEN_EXPIRED') {
          this.refreshFailed = true;
        }
        throw refreshError;
      }
    }

    return response;
  }
}

export const tokenManager = new TokenManager();
